// EvalContext — the engine's dependency-injection surface (§2.4).
//
// Every `evalExpr` call receives exactly one `EvalContext`. The shape
// is deliberately small and *hybrid*:
//
//   - callbacks for *live* state (resolved lazily at the moment an
//     op needs them — see §2.4 "Why hybrid"). Currently empty;
//     `getCogniteClient` and `getViews` land alongside the
//     query-knowledge-graph op (Step 4a) so the engine doesn't carry
//     cognite-specific imports it doesn't yet need.
//
//   - plain values for per-call configuration (the `session` and the
//     optional `signal`).
//
// `Session` carries conversation-scoped state (I/O memoization +
// dataset bindings between turns); see `session/Session.ts`. The
// `AbortSignal` is optional; when present, I/O ops cancel implicitly
// through `session.fetch` — op authors do not check `signal.aborted`
// directly (§2.4 last paragraph).
//
// The engine does *not* memoize callback results. Callers are
// responsible for callback stability (closing over refs that don't
// change unnecessarily). The engine's contract is "call when needed";
// no caching layer is interposed (§7.11 last paragraph).

import type { CogniteClient } from '@cognite/sdk';

import type { DataModelView } from '../cognite/view.ts';
import type { SourcePos } from './ast.ts';
import { MissingCallbackError } from './errors.ts';
import type { SourceDescriptor } from './plan.ts';
import { createInMemorySession } from './session/createInMemorySession.ts';
import type { Session } from './session/Session.ts';
import type { MaterializedDataset } from './value.ts';

/**
 * Context handed to a per-source-kind fetcher. Carries the session (for
 * downstream `session.fetch` calls inside the fetcher if it composes
 * smaller reads) and the optional cancellation signal from the
 * surrounding `EvalContext`.
 *
 * Kept narrow — fetchers should not receive the full `EvalContext`
 * because they must not recurse back into expression evaluation.
 */
export type SourceFetcherCtx = {
  readonly session: Session;
  readonly signal?: AbortSignal;
};

/**
 * Per-source-kind fetcher: given a source descriptor, return a
 * materialised `Dataset` whose `schema` describes the fetched columns.
 *
 * The generic `SourceType` lets a fetcher narrow to a single source kind
 * (e.g. `SourceFetcher<TsRefSource>`) for precise argument typing while
 * the registry below uses a discriminator-indexed mapped type to keep
 * each slot bound to its matching descriptor.
 */
export type SourceFetcher<SourceType extends SourceDescriptor> = (
  src: SourceType,
  ctx: SourceFetcherCtx,
) => Promise<MaterializedDataset>;

/**
 * Dispatch map from source-descriptor `kind` to its fetcher. The
 * materializer (sub-task 4-prep.4) looks up by `src.kind` and calls the
 * matching fetcher; an absent entry surfaces as
 * `MaterializationError('missing-fetcher', …)`.
 *
 * Entries are optional so test code (or 4-prep.4's first ship) can
 * populate only the kinds it exercises; production wiring lands per
 * source kind as its owning phase implements the real CDF integration
 * (ts-ref + qkg in Phase 4a; further kinds in later phases).
 */
export type SourceFetcherMap = {
  readonly [KindType in SourceDescriptor['kind']]?: SourceFetcher<Extract<SourceDescriptor, { kind: KindType }>>;
};

/**
 * Engine DI surface. One per `evalExpr` invocation.
 *
 * @see §2.4 — design rationale (hybrid callbacks + values)
 * @see §7.11 — React integration; `useEvalExpr()` constructs an
 *      `EvalContext` from React state and passes it through
 */
export type EvalContext = {
  /**
   * Conversation-scoped session. I/O ops route memoizable reads
   * through `session.fetch(key, fetcher)`; cross-turn dataset
   * bindings live on `session.getBinding`/`recordBinding`. The chat
   * plugin supplies a TanStack-Query-backed impl; non-React callers
   * use `createInMemorySession()`.
   */
  readonly session: Session;

  /**
   * Optional cancellation signal. Plumbed into `session.fetch`
   * implementations that support it (e.g. TanStack `fetchQuery`).
   * Ops do not consume the signal directly.
   */
  readonly signal?: AbortSignal;

  /**
   * Per-source-kind fetchers consulted by the materializer (sub-task
   * 4-prep.4) when reducing a `LazyDataset` to a `MaterializedDataset`.
   * Each entry knows how to turn a `SourceDescriptor` of its kind into
   * actual columns. Missing entries surface as `MaterializationError`
   * at materialisation time.
   *
   * Optional — `defaultEvalCtx()` leaves this `undefined`. Test code
   * populates with stubs; production wiring (CDF integrations) lands
   * per source kind alongside that source kind's owning phase.
   */
  readonly sourceFetchers?: SourceFetcherMap;

  /**
   * Lazy accessor for the CDF client (Phase 4-prep sub-task 4-prep.8).
   * Optional — non-CDF callers (tests, in-memory sessions) leave this
   * `undefined`. Ops that require a CDF client (qkg, from-view, …)
   * route through `requireCogniteClient(ctx, opName, pos)` so the
   * `MissingCallbackError` surfaces with a uniform "wire this callback
   * up" hint.
   *
   * Returned as a callback (not a value) so the React-side
   * `useEvalExpr` integration can defer reading the client until
   * actually needed — avoiding a render-time CDF dependency on
   * components that don't need it.
   */
  readonly getCogniteClient?: () => CogniteClient;

  /**
   * Lazy accessor for the data-model views available to the current
   * conversation (Phase 4-prep sub-task 4-prep.8). Used by `qkg`
   * (Phase 4a) to resolve a view name to its parsed schema. Same
   * callback-not-value rationale as `getCogniteClient`.
   *
   * Returned synchronously — the views list is expected to be
   * cheaply available (typically loaded once at conversation start).
   * Ops that require views route through `requireViews(ctx, opName,
   * pos)` for uniform error surfacing.
   */
  readonly getViews?: () => readonly DataModelView[];
};

/**
 * Construct a default `EvalContext` backed by a fresh in-memory
 * session. Used as:
 *
 *   - the `evalExpr` / `evalScript` parameter default — so test
 *     callers stay terse;
 *   - tests that need to *share* a session across multiple
 *     evaluations (so cross-call bindings or memoized fetches
 *     persist between invocations within one test).
 *
 * Production callers should construct their own `EvalContext` from
 * React state via `useEvalExpr()` — that path supplies a
 * TanStack-Query-backed session and (eventually) the
 * `getCogniteClient` / `getViews` callbacks.
 */
export const defaultEvalCtx = (): EvalContext => ({ session: createInMemorySession() });
/**
 * Narrow-or-throw helper for `EvalContext.getCogniteClient` (Phase
 * 4-prep sub-task 4-prep.8). Ops that need a CDF client call this at
 * the apply boundary so the `MissingCallbackError` surfaces with a
 * uniform "wire this callback up" hint, regardless of which op
 * needed it. Sibling of `requireViews`.
 */
export const requireCogniteClient = (ctx: EvalContext, op: string, pos: SourcePos): CogniteClient => {
  if (ctx.getCogniteClient === undefined) {
    throw new MissingCallbackError(op, 'getCogniteClient', pos);
  }
  return ctx.getCogniteClient();
};

/**
 * Narrow-or-throw helper for `EvalContext.getViews` (Phase 4-prep
 * sub-task 4-prep.8). Mirrors `requireCogniteClient`.
 */
export const requireViews = (ctx: EvalContext, op: string, pos: SourcePos): readonly DataModelView[] => {
  if (ctx.getViews === undefined) {
    throw new MissingCallbackError(op, 'getViews', pos);
  }
  return ctx.getViews();
};
